import { Component, inject, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { OnboardingService } from '../../core/services/onboarding.service';
import { ToastService } from '../../core/services/toast.service';
import { AuthService } from '../../core/services/auth.service';
import {
  OnboardingResponse, OnboardingRegisterRequest,
  OnboardingStatus, MemberClass, FileDto
} from '../../core/models/models';
import { ModalComponent, ModalErrorState } from '../../shared/components/modal/modal.component';
import { extractErrorDetails } from '../../core/interceptors/response.interceptor';
import { StatusBadgeComponent } from '../../shared/components/status-badge/status-badge.component';
import { PhoneInputComponent, isValidPhoneNumber } from '../../shared/components/phone-input/phone-input.component';

interface ParsedDocument {
  content: string;
  fileType: string;
  dataUrl: string;
  fileName: string;
  isPdf: boolean;
  isImage: boolean;
}

@Component({
  selector: 'app-onboarding',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, StatusBadgeComponent, PhoneInputComponent, ModalComponent],
  template: `
    <div class="min-h-screen bg-slate-50/60 pb-16">
      <!-- Portal Top Bar for Public & Standalone Access -->
      <nav class="bg-white border-b border-slate-200 px-6 py-3.5 mb-6 shadow-2xs">
        <div class="max-w-6xl mx-auto flex items-center justify-between">
          <a routerLink="/" class="flex items-center gap-3 hover:opacity-90 transition">
            <img src="/img/smz.png" class="w-9 h-auto" alt="SMZ Government Emblem" />
            <div>
              <span class="text-[10px] font-black uppercase tracking-widest text-emerald-800 block leading-tight">Zanzibar e-Gov Portal</span>
              <span class="text-sm font-extrabold text-slate-900 leading-none">Zan-X-Change Consent Management</span>
            </div>
          </a>

          <div class="flex items-center gap-3">
            @if (authService.isLoggedIn()) {
              <a
                routerLink="/dashboard"
                class="px-3.5 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs inline-flex items-center gap-1.5 transition"
              >
                ← Back to Portal Dashboard
              </a>
            } @else {
              <a
                routerLink="/login"
                class="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs inline-flex items-center gap-1.5 shadow-sm transition"
              >
                <span>Sign In to Portal →</span>
              </a>
            }
          </div>
        </div>
      </nav>

      <div class="max-w-6xl mx-auto px-4 sm:px-6 space-y-6 font-sans">
        <!-- Header -->
        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 class="text-2xl font-extrabold text-slate-900 tracking-tight">Institution Registration & Onboarding</h1>
            <p class="text-xs text-slate-500">Formal onboarding portal for government agencies, commercial entities, and NGOs onto Zanzibar e-Gov Interoperability Framework.</p>
          </div>

        <div class="flex items-center gap-2 p-1 rounded-xl bg-slate-100 border border-slate-200">
          <button
            (click)="activeView.set('register')"
            class="px-4 py-2 rounded-lg text-xs font-bold transition-all"
            [ngClass]="activeView() === 'register' ? 'bg-emerald-600 text-white shadow-2xs' : 'text-slate-600 hover:text-slate-900'"
          >
            Registration Wizard
          </button>
          <button
            (click)="activeView.set('lookup')"
            class="px-4 py-2 rounded-lg text-xs font-bold transition-all"
            [ngClass]="activeView() === 'lookup' ? 'bg-emerald-600 text-white shadow-2xs' : 'text-slate-600 hover:text-slate-900'"
          >
            Status Tracker
          </button>

          @if (activeView() === 'edit') {
            <button
              class="px-4 py-2 rounded-lg text-xs font-bold bg-amber-600 text-white shadow-2xs flex items-center gap-1.5"
            >
              <span>Edit & Resubmit</span>
            </button>
          }

          @if (authService.canManageOnboarding()) {
            <button
              (click)="activeView.set('queue')"
              class="px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5"
              [ngClass]="activeView() === 'queue' ? 'bg-amber-600 text-white shadow-2xs' : 'text-slate-600 hover:text-slate-900'"
            >
              Regulator Approval Queue
              <span class="px-1.5 py-0.5 rounded-full text-[10px] bg-amber-100 text-amber-800 font-bold">{{ pendingQueue().length }}</span>
            </button>
          }
        </div>
      </div>

      <!-- VIEW 1: Multi-Step Registration Wizard -->
      @if (activeView() === 'register') {
        <div class="glass-panel p-8 rounded-3xl border border-slate-200 bg-white max-w-4xl mx-auto space-y-8 shadow-sm">
          <!-- Wizard Stepper Indicator -->
          <div class="flex items-center justify-between border-b border-slate-200 pb-6 px-4">
            <div class="flex items-center gap-3">
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs"
                [ngClass]="currentStep() >= 1 ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-500'"
              >
                1
              </div>
              <div>
                <div class="text-xs font-bold text-slate-800">Institution Identity</div>
                <div class="text-[10px] text-slate-400">Sector & Official Profile</div>
              </div>
            </div>

            <div class="h-0.5 w-12 bg-slate-200" [ngClass]="{'!bg-emerald-600': currentStep() >= 2}"></div>

            <div class="flex items-center gap-3">
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs"
                [ngClass]="currentStep() >= 2 ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-500'"
              >
                2
              </div>
              <div>
                <div class="text-xs font-bold text-slate-800">Representative</div>
                <div class="text-[10px] text-slate-400">Authorized Officer</div>
              </div>
            </div>

            <div class="h-0.5 w-12 bg-slate-200" [ngClass]="{'!bg-emerald-600': currentStep() >= 3}"></div>

            <div class="flex items-center gap-3">
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs"
                [ngClass]="currentStep() >= 3 ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-500'"
              >
                3
              </div>
              <div>
                <div class="text-xs font-bold text-slate-800">Compliance Doc</div>
                <div class="text-[10px] text-slate-400">Application Letter</div>
              </div>
            </div>
          </div>

          <!-- Step 1: Institution Details -->
          @if (currentStep() === 1) {
            <div class="space-y-6">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="sm:col-span-2">
                  <label class="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Institution Official Name <span class="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    [(ngModel)]="registerForm.institutionName"
                    placeholder="e.g. Ministry of Infrastructure, Communications and Transport"
                    class="w-full px-4 py-2.5 rounded-xl border border-slate-300 text-xs text-slate-900 focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label class="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Sector / Member Class <span class="text-rose-500">*</span>
                  </label>
                  <select
                    [(ngModel)]="registerForm.memberClassCode"
                    class="w-full px-4 py-2.5 rounded-xl border border-slate-300 text-xs text-slate-900 focus:border-emerald-500 bg-white"
                  >
                    @for (mc of memberClasses; track mc.code) {
                      <option [value]="mc.code">{{ mc.name }} ({{ mc.code }})</option>
                    }
                  </select>
                </div>

                <div>
                  <label class="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Vote Number / Reg Number
                  </label>
                  <input
                    type="text"
                    [(ngModel)]="registerForm.voteNumber"
                    placeholder="e.g. V-504"
                    class="w-full px-4 py-2.5 rounded-xl border border-slate-300 text-xs text-slate-900 focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label class="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Official Institution Email <span class="text-rose-500">*</span>
                  </label>
                  <input
                    type="email"
                    [(ngModel)]="registerForm.institutionEmail"
                    placeholder="info@institution.go.tz"
                    class="w-full px-4 py-2.5 rounded-xl border border-slate-300 text-xs text-slate-900 focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label class="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Official Institution Phone <span class="text-rose-500">*</span>
                  </label>
                  <app-phone-input
                    [(ngModel)]="registerForm.institutionPhone"
                    [required]="true"
                    placeholder="777 000 000"
                  ></app-phone-input>
                </div>
              </div>

              <div class="flex justify-end pt-4">
                <button
                  (click)="goToStep(2)"
                  class="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-sm"
                >
                  Next: Representative Info →
                </button>
              </div>
            </div>
          }

          <!-- Step 2: Representative Contact Details -->
          @if (currentStep() === 2) {
            <div class="space-y-6">
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label class="block text-xs font-bold text-slate-700 uppercase mb-1">First Name *</label>
                  <input
                    type="text"
                    [(ngModel)]="registerForm.firstName"
                    placeholder="First name"
                    class="w-full px-4 py-2.5 rounded-xl border border-slate-300 text-xs text-slate-900 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Middle Name</label>
                  <input
                    type="text"
                    [(ngModel)]="registerForm.middleName"
                    placeholder="Middle name"
                    class="w-full px-4 py-2.5 rounded-xl border border-slate-300 text-xs text-slate-900 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Last Name *</label>
                  <input
                    type="text"
                    [(ngModel)]="registerForm.lastName"
                    placeholder="Last name"
                    class="w-full px-4 py-2.5 rounded-xl border border-slate-300 text-xs text-slate-900 focus:border-emerald-500"
                  />
                </div>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Representative Phone *</label>
                  <app-phone-input
                    [(ngModel)]="registerForm.phone"
                    [required]="true"
                    placeholder="777 000 000"
                  ></app-phone-input>
                </div>
                <div>
                  <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Representative Email</label>
                  <input
                    type="email"
                    [(ngModel)]="registerForm.applicantEmail"
                    placeholder="rep@institution.go.tz"
                    class="w-full px-4 py-2.5 rounded-xl border border-slate-300 text-xs text-slate-900 focus:border-emerald-500"
                  />
                </div>
              </div>

              <div class="flex justify-between pt-4">
                <button
                  (click)="currentStep.set(1)"
                  class="px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs"
                >
                  ← Back
                </button>
                <button
                  (click)="goToStep(3)"
                  class="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-sm"
                >
                  Next: Compliance Document →
                </button>
              </div>
            </div>
          }

          <!-- Step 3: Application Letter Upload -->
          @if (currentStep() === 3) {
            <div class="space-y-6">
              <div class="p-6 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50/50 text-center space-y-4">
                <div class="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-700 mx-auto flex items-center justify-center">
                  <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <div>
                  <h4 class="text-sm font-bold text-slate-900">Upload Formal Institution Application Letter *</h4>
                  <p class="text-xs text-slate-500 mt-1">Official letter requesting integration onto Zanzibar e-Gov Interoperability Framework (PDF or Image, max 10MB).</p>
                </div>

                @if (!selectedFileName) {
                  <div>
                    <label class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-sm cursor-pointer transition">
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                      </svg>
                      <span>Choose Application Document</span>
                      <input type="file" (change)="onFileSelected($event)" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" class="hidden" />
                    </label>
                  </div>
                } @else {
                  <div class="p-4 rounded-xl bg-white border border-emerald-200 inline-flex items-center gap-4 text-left shadow-2xs">
                    <div class="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-700 font-extrabold flex items-center justify-center text-xs uppercase">
                      {{ selectedFileExt }}
                    </div>
                    <div>
                      <div class="font-bold text-xs text-slate-900">{{ selectedFileName }}</div>
                      <div class="text-[10px] text-slate-400 font-mono">{{ selectedFileSize }}</div>
                    </div>
                    <button
                      (click)="removeSelectedFile()"
                      class="p-1 rounded-lg text-rose-500 hover:bg-rose-50 transition"
                      title="Remove file"
                    >
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                }
              </div>

              <div class="flex justify-between pt-4">
                <button
                  (click)="currentStep.set(2)"
                  class="px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs"
                >
                  ← Back
                </button>
                <button
                  (click)="onSubmitRegistration()"
                  [disabled]="isSubmitting || !selectedFileName"
                  class="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-sm disabled:opacity-50 inline-flex items-center gap-2"
                >
                  @if (isSubmitting) {
                    <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span>Submitting Application...</span>
                  } @else {
                    <span>Submit Application for Regulatory Review</span>
                  }
                </button>
              </div>
            </div>
          }

          <!-- Step 4: Submission Confirmation Screen -->
          @if (currentStep() === 4 && submittedOnboarding(); as result) {
            <div class="p-8 rounded-2xl bg-gradient-to-b from-emerald-50/50 to-teal-50/30 border border-emerald-200 text-center space-y-5">
              <div class="w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 mx-auto flex items-center justify-center shadow-2xs">
                <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 class="text-xl font-extrabold text-slate-900">Application Submitted for Review!</h3>
                <p class="text-xs text-slate-600 mt-1 max-w-lg mx-auto">
                  Your institution onboarding request for <strong>{{ result.institutionName }}</strong> has been registered and forwarded to the eGAZ regulator review queue.
                </p>
              </div>

              <div class="p-4 rounded-xl bg-white border border-slate-200 max-w-md mx-auto space-y-2 text-left">
                <span class="text-[10px] font-bold uppercase text-slate-400 block">Your Application Tracking Token</span>
                <div class="text-base font-mono font-bold text-slate-900 bg-slate-50 p-2.5 rounded-lg border border-slate-200 select-all">
                  {{ result.token }}
                </div>
                <p class="text-[11px] text-slate-500">Save this token to track your application status or to make requested corrections.</p>
              </div>

              <div class="pt-2 flex justify-center gap-3">
                <button
                  (click)="trackSpecificToken(result.token)"
                  class="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-sm"
                >
                  Track Status Now →
                </button>
              </div>
            </div>
          }
        </div>
      }

      <!-- VIEW 2: Status Tracker / Lookup View -->
      @if (activeView() === 'lookup') {
        <div class="glass-panel p-8 rounded-3xl border border-slate-200 bg-white max-w-3xl mx-auto space-y-6 shadow-sm">
          <div>
            <h2 class="text-lg font-bold text-slate-900 tracking-wide">Track Onboarding Application Status</h2>
            <p class="text-xs text-slate-500 mt-0.5">For security, please enter your secret application tracking token issued upon registration or sent via email notification.</p>
          </div>

          <div class="flex gap-3">
            <input
              type="text"
              [(ngModel)]="lookupQuery"
              (input)="lookupError.set(null)"
              (keyup.enter)="onLookupStatus()"
              placeholder="Enter Secret Application Tracking Token (e.g. 8f2a...)"
              class="flex-1 px-4 py-2.5 rounded-xl bg-white border border-slate-300 text-xs text-slate-900 placeholder-slate-400 font-mono focus:border-emerald-500"
            />
            <button
              (click)="onLookupStatus()"
              [disabled]="isSearchingStatus || !lookupQuery.trim()"
              class="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-sm disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              @if (isSearchingStatus) {
                <svg class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              }
              <span>Check Status</span>
            </button>
          </div>

          <!-- Backend Response Error Display (When success is false / error occurred) -->
          @if (lookupError(); as err) {
            <div class="p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-900 space-y-2 animate-in fade-in duration-200">
              <div class="flex items-start gap-3">
                <div class="p-2 bg-rose-100 text-rose-600 rounded-xl shrink-0 mt-0.5">
                  <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div class="flex-1 space-y-1">
                  <div class="flex items-center justify-between gap-2">
                    <h3 class="font-bold text-xs text-rose-900 tracking-wide uppercase">Backend Response: Lookup Failed</h3>
                    @if (err.code) {
                      <span class="px-2 py-0.5 rounded-md bg-rose-200/80 font-mono text-[10px] font-bold text-rose-800 tracking-wider">{{ err.code }}</span>
                    }
                  </div>
                  <p class="text-xs text-rose-700 leading-relaxed font-medium">{{ err.message }}</p>
                  <p class="text-[11px] text-rose-600/80 pt-0.5">
                    Please verify your secret application tracking token and try again.
                  </p>
                </div>
              </div>
            </div>
          }

          @if (lookupResult(); as item) {
            <div class="p-6 rounded-2xl bg-slate-50 border border-slate-200 space-y-5 text-xs">
              <!-- Status Header Banner -->
              <div class="flex items-center justify-between border-b border-slate-200 pb-3">
                <div>
                  <span class="font-extrabold text-slate-900 text-sm block">{{ item.institutionName }}</span>
                  <span class="text-[11px] text-slate-500 font-mono">Sector: {{ item.memberClassName || item.memberClassCode || 'GOV' }}</span>
                </div>
                <app-status-badge [status]="item.status"></app-status-badge>
              </div>

              <!-- Action Banner: RETURNED FOR CORRECTION -->
              @if (item.status === OnboardingStatus.CORRECTION) {
                <div class="p-4 rounded-xl bg-amber-50 border border-amber-300 text-amber-900 space-y-3 shadow-2xs">
                  <div class="flex items-center gap-2 font-bold text-xs uppercase tracking-wide text-amber-800">
                    <svg class="w-4 h-4 text-amber-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span>Action Required: Application Returned for Correction</span>
                  </div>
                  <div class="p-3 bg-white/80 rounded-lg border border-amber-200 text-xs text-amber-950 font-medium">
                    <span class="font-bold text-[11px] block uppercase text-amber-800 mb-1">Regulator Review Remarks:</span>
                    <p class="whitespace-pre-wrap">{{ item.rejectionReason || 'Please review and update your institution information or compliance documents as requested.' }}</p>
                  </div>
                  <div class="pt-1 flex items-center justify-end">
                    <button
                      (click)="startCorrectionEdit(item)"
                      class="px-5 py-2 rounded-xl bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs shadow-sm inline-flex items-center gap-1.5 cursor-pointer"
                    >
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span>Edit & Resubmit Application Now →</span>
                    </button>
                  </div>
                </div>
              }

              <!-- Action Banner: REJECTED -->
              @if (item.status === OnboardingStatus.REJECTED) {
                <div class="p-4 rounded-xl bg-rose-50 border border-rose-300 text-rose-900 space-y-2">
                  <div class="font-bold text-xs uppercase tracking-wide text-rose-800 flex items-center gap-2">
                    <svg class="w-4 h-4 text-rose-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Application Rejected by Regulator</span>
                  </div>
                  <div class="p-3 bg-white/80 rounded-lg border border-rose-200 text-xs text-rose-950">
                    <span class="font-bold text-[11px] block uppercase text-rose-800 mb-1">Rejection Reason:</span>
                    <p class="whitespace-pre-wrap">{{ item.rejectionReason || 'The application did not fulfill regulatory compliance prerequisites.' }}</p>
                  </div>
                </div>
              }

              <!-- Action Banner: PENDING -->
              @if (item.status === OnboardingStatus.PENDING) {
                <div class="p-3.5 rounded-xl bg-teal-50 border border-teal-200 text-teal-800 flex items-center gap-2.5">
                  <svg class="w-4 h-4 text-teal-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Your application is currently pending verification in the eGAZ regulator queue. You will receive an email once decision is made.</span>
                </div>
              }

              <!-- Action Banner: APPROVED -->
              @if (item.status === OnboardingStatus.APPROVED) {
                <div class="p-4 rounded-xl bg-emerald-50 border border-emerald-300 text-emerald-900 space-y-2">
                  <div class="font-bold text-xs uppercase tracking-wide text-emerald-800 flex items-center gap-2">
                    <svg class="w-4 h-4 text-emerald-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Application Approved!</span>
                  </div>
                  <p class="text-xs text-emerald-900">Your institution is authorized on the Zan-X-Change platform. Please complete initial administrator account credentials setup:</p>
                  @if (item.token) {
                    <div class="pt-1">
                      <a
                        [routerLink]="['/onboard-user', item.token]"
                        class="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs inline-flex items-center gap-1.5"
                      >
                        <span>Set Up Administrator Password →</span>
                      </a>
                    </div>
                  }
                </div>
              }

              <!-- Application Details Grid -->
              <div class="grid grid-cols-2 gap-4 pt-2">
                <div>
                  <span class="text-slate-400 block text-[11px]">Vote Number:</span>
                  <span class="font-mono font-bold text-slate-800">{{ item.voteNumber || 'N/A' }}</span>
                </div>
                <div>
                  <span class="text-slate-400 block text-[11px]">Institution Email:</span>
                  <span class="font-mono text-slate-800">{{ item.institutionEmail }}</span>
                </div>
                <div>
                  <span class="text-slate-400 block text-[11px]">Applicant Officer:</span>
                  <span class="font-medium text-slate-800">{{ item.applicantName || 'N/A' }}</span>
                </div>
                <div>
                  <span class="text-slate-400 block text-[11px]">Applicant Phone:</span>
                  <span class="font-mono text-slate-800">{{ item.phone }}</span>
                </div>
                <div>
                  <span class="text-slate-400 block text-[11px]">Tracking Token:</span>
                  <span class="font-mono text-emerald-800 font-bold select-all">{{ item.token }}</span>
                </div>
                <div>
                  <span class="text-slate-400 block text-[11px]">Application Document:</span>
                  @if (item.applicationLetter) {
                    <button
                      (click)="inspectItemDocument(item)"
                      class="text-emerald-700 hover:underline font-bold inline-flex items-center gap-1"
                    >
                      <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <span>Preview Attached Document</span>
                    </button>
                  } @else {
                    <span class="text-slate-400 italic">None attached</span>
                  }
                </div>
              </div>

              <!-- INSTITUTION ACTION: Edit & Resubmit when Pending or Returned -->
              @if (!authService.hasRole('SuperAdmin') && !authService.isInternal() && item.status === OnboardingStatus.PENDING) {
                <div class="pt-2 flex items-center justify-end border-t border-slate-200">
                  <button
                    (click)="startCorrectionEdit(item)"
                    class="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs inline-flex items-center gap-1.5 shadow-2xs cursor-pointer"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span>Edit & Resubmit Application</span>
                  </button>
                </div>
              }

              <!-- SUPER ADMIN DECISION PANEL (Only SuperAdmin or Internal Reviewer) -->
              @if (authService.canManageOnboarding()) {
                <div class="p-4 rounded-xl bg-slate-100 border border-slate-300 space-y-2 mt-2">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-1.5 font-bold text-xs uppercase tracking-wide text-slate-800">
                      <svg class="w-4 h-4 text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <span>Regulator Review & Decision Panel (Super Admin Only)</span>
                    </div>
                    <span class="text-[11px] text-slate-500 font-mono">Status: {{ item.status }}</span>
                  </div>
                  <div class="flex flex-wrap items-center justify-end gap-2.5 pt-2 border-t border-slate-200">
                    @if (item.status === OnboardingStatus.PENDING || item.status === OnboardingStatus.ON_PROGRESS || item.status === OnboardingStatus.CORRECTION) {
                      <button
                        (click)="openDecisionModal(item.uid, 'reject')"
                        class="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs shadow-2xs inline-flex items-center gap-1.5 cursor-pointer"
                      >
                        <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <span>Reject Application</span>
                      </button>
                      <button
                        (click)="openDecisionModal(item.uid, 'correction')"
                        class="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-white font-bold text-xs shadow-2xs inline-flex items-center gap-1.5 cursor-pointer"
                      >
                        <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <span>Return for Correction</span>
                      </button>
                      <button
                        (click)="onApproveOnboarding(item.uid)"
                        class="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-sm inline-flex items-center gap-1.5 cursor-pointer"
                      >
                        <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Approve Institution</span>
                      </button>
                    }
                  </div>
                </div>
              }
            </div>
          }
        </div>
      }

      <!-- VIEW 3: Edit & Resubmit Application Form (Correction Mode) -->
      @if (activeView() === 'edit' && editTarget(); as target) {
        <div class="glass-panel p-8 rounded-3xl border border-amber-200 bg-white max-w-4xl mx-auto space-y-6 shadow-sm">
          <!-- Correction Header Banner -->
          <div class="p-4 rounded-2xl bg-amber-50 border border-amber-300 space-y-2">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2 text-amber-900 font-bold text-xs uppercase tracking-wide">
                <svg class="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span>Editing Application for Resubmission</span>
              </div>
              <button
                (click)="activeView.set('lookup')"
                class="text-xs text-slate-500 hover:text-slate-800 font-semibold"
              >
                Cancel & Return
              </button>
            </div>
            <div class="p-3 bg-white/80 rounded-xl border border-amber-200 text-xs text-amber-950 font-medium">
              <span class="font-bold text-[11px] block uppercase text-amber-800 mb-0.5">Regulator's Instructions to Correct:</span>
              <p class="whitespace-pre-wrap">{{ target.rejectionReason || 'Please amend the requested information or attach the appropriate official documents.' }}</p>
            </div>
          </div>

          <!-- Edit Form -->
          <div class="space-y-5 text-xs">
            <h3 class="font-bold text-sm text-slate-900 border-b border-slate-200 pb-2">1. Institution Identity</h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="sm:col-span-2">
                <label class="block font-bold text-slate-700 uppercase mb-1">Institution Legal Name *</label>
                <input
                  type="text"
                  [(ngModel)]="registerForm.institutionName"
                  class="w-full px-4 py-2.5 rounded-xl border border-slate-300 text-slate-900 focus:border-amber-500"
                />
              </div>

              <div>
                <label class="block font-bold text-slate-700 uppercase mb-1">Sector / Member Class *</label>
                <select
                  [(ngModel)]="registerForm.memberClassCode"
                  class="w-full px-4 py-2.5 rounded-xl border border-slate-300 text-slate-900 focus:border-amber-500 bg-white"
                >
                  @for (mc of memberClasses; track mc.code) {
                    <option [value]="mc.code">{{ mc.name }} ({{ mc.code }})</option>
                  }
                </select>
              </div>

              <div>
                <label class="block font-bold text-slate-700 uppercase mb-1">Vote Number / Reg Number</label>
                <input
                  type="text"
                  [(ngModel)]="registerForm.voteNumber"
                  class="w-full px-4 py-2.5 rounded-xl border border-slate-300 text-slate-900 focus:border-amber-500"
                />
              </div>

              <div>
                <label class="block font-bold text-slate-700 uppercase mb-1">Official Email *</label>
                <input
                  type="email"
                  [(ngModel)]="registerForm.institutionEmail"
                  class="w-full px-4 py-2.5 rounded-xl border border-slate-300 text-slate-900 focus:border-amber-500"
                />
              </div>

              <div>
                <label class="block font-bold text-slate-700 uppercase mb-1">Official Phone *</label>
                <app-phone-input
                  [(ngModel)]="registerForm.institutionPhone"
                  [required]="true"
                ></app-phone-input>
              </div>
            </div>

            <h3 class="font-bold text-sm text-slate-900 border-b border-slate-200 pb-2 pt-3">2. Representative Contact</h3>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label class="block font-bold text-slate-700 uppercase mb-1">First Name *</label>
                <input
                  type="text"
                  [(ngModel)]="registerForm.firstName"
                  class="w-full px-4 py-2.5 rounded-xl border border-slate-300 text-slate-900 focus:border-amber-500"
                />
              </div>
              <div>
                <label class="block font-bold text-slate-700 uppercase mb-1">Middle Name</label>
                <input
                  type="text"
                  [(ngModel)]="registerForm.middleName"
                  class="w-full px-4 py-2.5 rounded-xl border border-slate-300 text-slate-900 focus:border-amber-500"
                />
              </div>
              <div>
                <label class="block font-bold text-slate-700 uppercase mb-1">Last Name *</label>
                <input
                  type="text"
                  [(ngModel)]="registerForm.lastName"
                  class="w-full px-4 py-2.5 rounded-xl border border-slate-300 text-slate-900 focus:border-amber-500"
                />
              </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="block font-bold text-slate-700 uppercase mb-1">Representative Phone *</label>
                <app-phone-input
                  [(ngModel)]="registerForm.phone"
                  [required]="true"
                ></app-phone-input>
              </div>
              <div>
                <label class="block font-bold text-slate-700 uppercase mb-1">Representative Email</label>
                <input
                  type="email"
                  [(ngModel)]="registerForm.applicantEmail"
                  class="w-full px-4 py-2.5 rounded-xl border border-slate-300 text-slate-900 focus:border-amber-500"
                />
              </div>
            </div>

            <h3 class="font-bold text-sm text-slate-900 border-b border-slate-200 pb-2 pt-3">3. Application Letter Document</h3>
            <div class="p-5 rounded-2xl border-2 border-dashed border-amber-300 bg-amber-50/40 text-center space-y-3">
              @if (target.applicationLetter && !selectedFileName) {
                <div class="p-3 bg-white rounded-xl border border-amber-200 inline-flex items-center gap-3 text-left">
                  <div class="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-800 font-extrabold flex items-center justify-center text-xs">
                    DOC
                  </div>
                  <div>
                    <span class="text-xs font-bold text-slate-800 block">Existing Document Attached</span>
                    <span class="text-[10px] text-slate-500">You may keep this document or upload a replacement below.</span>
                  </div>
                  <button
                    (click)="inspectItemDocument(target)"
                    type="button"
                    class="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-[11px] font-bold transition"
                  >
                    Preview
                  </button>
                </div>
              }

              @if (selectedFileName) {
                <div class="p-3 bg-white rounded-xl border border-emerald-300 inline-flex items-center gap-3 text-left shadow-2xs">
                  <div class="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-800 font-extrabold flex items-center justify-center text-xs uppercase">
                    {{ selectedFileExt }}
                  </div>
                  <div>
                    <div class="font-bold text-xs text-slate-900">New File: {{ selectedFileName }}</div>
                    <div class="text-[10px] text-slate-400 font-mono">{{ selectedFileSize }}</div>
                  </div>
                  <button
                    (click)="removeSelectedFile()"
                    class="p-1 rounded-lg text-rose-500 hover:bg-rose-50"
                  >
                    ✕
                  </button>
                </div>
              }

              <div>
                <label class="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs shadow-2xs cursor-pointer transition">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  <span>{{ target.applicationLetter ? 'Upload Replacement Document' : 'Upload Application Document' }}</span>
                  <input type="file" (change)="onFileSelected($event)" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" class="hidden" />
                </label>
              </div>
            </div>

            <div class="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <button
                (click)="activeView.set('lookup')"
                class="px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs"
              >
                Cancel
              </button>
              <button
                (click)="onSubmitResubmission(target.token!)"
                [disabled]="isSubmitting"
                class="px-6 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs shadow-sm inline-flex items-center gap-2"
              >
                @if (isSubmitting) {
                  <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Resubmitting...</span>
                } @else {
                  <span>Resubmit Corrected Application →</span>
                }
              </button>
            </div>
          </div>
        </div>
      }

      <!-- VIEW 4: Regulator Approval Queue (for SuperAdmin/Internal) -->
      @if (activeView() === 'queue') {
        <div class="glass-panel rounded-3xl border border-slate-200 bg-white overflow-hidden shadow-sm">
          <div class="p-6 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h2 class="text-lg font-bold text-slate-900 tracking-wide">eGAZ Regulator Onboarding Verification Queue</h2>
              <p class="text-xs text-slate-500">Inspect institution applications, review uploaded compliance letters, and process approval or correction workflows.</p>
            </div>
            <button
              (click)="loadQueue()"
              class="px-3.5 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs inline-flex items-center gap-1.5 self-start sm:self-auto"
            >
              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Refresh Queue</span>
            </button>
          </div>

          <div class="overflow-x-auto">
            <table class="w-full text-left border-collapse text-xs">
              <thead>
                <tr class="bg-slate-100/80 text-slate-600 uppercase tracking-wider font-bold border-b border-slate-200">
                  <th class="p-4">Vote No.</th>
                  <th class="p-4">Institution Name</th>
                  <th class="p-4">Sector</th>
                  <th class="p-4">Representative</th>
                  <th class="p-4">Contact Phone</th>
                  <th class="p-4">Compliance Doc</th>
                  <th class="p-4">Status</th>
                  <th class="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-200/80">
                @if (pendingQueue().length === 0) {
                  <tr>
                    <td colspan="8" class="p-8 text-center text-slate-400 font-medium">
                      No onboarding applications in the verification queue.
                    </td>
                  </tr>
                }
                @for (onb of pendingQueue(); track onb.uid) {
                  <tr class="hover:bg-emerald-50/30 transition-colors">
                    <td class="p-4 font-mono font-bold text-emerald-700">{{ onb.voteNumber || 'N/A' }}</td>
                    <td class="p-4 font-bold text-slate-900">{{ onb.institutionName }}</td>
                    <td class="p-4">
                      <span class="px-2 py-0.5 rounded text-[10px] font-extrabold uppercase bg-slate-100 text-slate-700 border border-slate-200">
                        {{ onb.memberClassCode || 'GOV' }}
                      </span>
                    </td>
                    <td class="p-4 text-slate-700">{{ onb.applicantName }}</td>
                    <td class="p-4 font-mono text-slate-500">{{ onb.phone }}</td>
                    <td class="p-4">
                      @if (onb.applicationLetter) {
                        <button
                          (click)="onInspectOnboarding(onb)"
                          class="px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-bold text-[11px] inline-flex items-center gap-1 border border-emerald-200 transition"
                        >
                          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          <span>Preview Doc</span>
                        </button>
                      } @else {
                        <span class="text-slate-400 italic text-[11px]">No doc</span>
                      }
                    </td>
                    <td class="p-4">
                      <app-status-badge [status]="onb.status"></app-status-badge>
                    </td>
                    <td class="p-4 text-right">
                      <div class="inline-flex items-center gap-1.5 justify-end">
                        <!-- Inspect & Review Button -->
                        <button
                          (click)="onInspectOnboarding(onb)"
                          title="Inspect application and review document"
                          class="px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-emerald-50 hover:text-emerald-700 text-slate-700 font-semibold text-xs border border-slate-200 transition inline-flex items-center gap-1 cursor-pointer"
                        >
                          <svg class="w-3.5 h-3.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          <span>Inspect</span>
                        </button>

                        @if (onb.status === OnboardingStatus.PENDING || onb.status === OnboardingStatus.ON_PROGRESS) {
                          <!-- Quick Approve Button -->
                          <button
                            (click)="onApproveOnboarding(onb.uid)"
                            title="Approve Onboarding"
                            class="px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-2xs cursor-pointer"
                          >
                            Approve
                          </button>

                          <!-- Return for Correction Button -->
                          <button
                            (click)="openDecisionModal(onb.uid, 'correction')"
                            title="Return for Correction with Remarks"
                            class="px-2.5 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-white font-bold text-xs shadow-2xs cursor-pointer"
                          >
                            Return
                          </button>

                          <!-- Reject Button -->
                          <button
                            (click)="openDecisionModal(onb.uid, 'reject')"
                            title="Reject Application with Reason"
                            class="px-2.5 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs shadow-2xs cursor-pointer"
                          >
                            Reject
                          </button>
                        }
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      }

      <!-- INSPECT APPLICATION & DOCUMENT PREVIEW MODAL -->
      <app-modal
        [isOpen]="showInspectModal()"
        title="Institution Onboarding Compliance Inspection"
        maxWidth="max-w-4xl"
        (close)="showInspectModal.set(false)"
      >
        @if (selectedInspection(); as inst) {
          <div class="space-y-6 text-xs text-slate-700">
            <!-- Institution Overview Header -->
            <div class="flex items-center justify-between p-4 rounded-2xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200">
              <div>
                <h3 class="text-base font-bold text-slate-900">{{ inst.institutionName }}</h3>
                <div class="flex items-center gap-2 mt-1">
                  <span class="px-2 py-0.5 rounded text-[10px] font-extrabold uppercase bg-emerald-100 text-emerald-800 border border-emerald-300">
                    {{ inst.memberClassName || inst.memberClassCode || 'GOV' }}
                  </span>
                  <span class="font-mono text-slate-500 text-[11px]">Vote: {{ inst.voteNumber || 'N/A' }}</span>
                  <span class="font-mono text-slate-500 text-[11px]">• Reg: {{ inst.registrationNumber || 'N/A' }}</span>
                </div>
              </div>
              <div>
                <app-status-badge [status]="inst.status"></app-status-badge>
              </div>
            </div>

            <!-- Profile Summary Grid -->
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div class="p-3 rounded-xl bg-slate-50 border border-slate-200">
                <span class="text-[10px] uppercase font-bold text-slate-400 block mb-0.5">Official Email</span>
                <span class="font-mono font-semibold text-slate-900 block truncate" [title]="inst.institutionEmail">{{ inst.institutionEmail }}</span>
              </div>
              <div class="p-3 rounded-xl bg-slate-50 border border-slate-200">
                <span class="text-[10px] uppercase font-bold text-slate-400 block mb-0.5">Official Phone</span>
                <span class="font-mono font-semibold text-slate-900">{{ inst.institutionPhone }}</span>
              </div>
              <div class="p-3 rounded-xl bg-slate-50 border border-slate-200">
                <span class="text-[10px] uppercase font-bold text-slate-400 block mb-0.5">Representative</span>
                <span class="font-semibold text-slate-900">{{ inst.applicantName || 'N/A' }}</span>
              </div>
              <div class="p-3 rounded-xl bg-slate-50 border border-slate-200">
                <span class="text-[10px] uppercase font-bold text-slate-400 block mb-0.5">Rep Phone</span>
                <span class="font-mono font-semibold text-slate-900">{{ inst.phone }}</span>
              </div>
            </div>

            <!-- Reviewer Notes if already returned/rejected -->
            @if (inst.rejectionReason) {
              <div class="p-3.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-950">
                <span class="font-bold text-[10px] uppercase text-amber-800 block mb-0.5">Current Regulator Remarks:</span>
                <p class="whitespace-pre-wrap">{{ inst.rejectionReason }}</p>
              </div>
            }

            <!-- Application Letter Compliance Document Preview Section -->
            <div class="space-y-3 border-t border-slate-200 pt-4">
              <div class="flex items-center justify-between">
                <div>
                  <h4 class="font-bold text-sm text-slate-900">Application Letter & Compliance Attachment</h4>
                  <p class="text-[11px] text-slate-500">Official formal registration letter signed by accounting officer.</p>
                </div>

                @if (parsedDocument(); as doc) {
                  <button
                    (click)="downloadParsedDocument(doc)"
                    class="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs inline-flex items-center gap-1.5 shadow-2xs"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span>Download Attachment</span>
                  </button>
                }
              </div>

              @if (parsedDocument(); as doc) {
                <!-- Embedded Preview Container -->
                <div class="rounded-2xl border border-slate-200 overflow-hidden bg-slate-100 p-2">
                  @if (doc.isPdf && sanitizedDocumentUrl()) {
                    <iframe
                      [src]="sanitizedDocumentUrl()"
                      class="w-full h-[460px] rounded-xl border border-slate-300 bg-white"
                      title="Application Letter PDF"
                    ></iframe>
                  } @else if (doc.isImage) {
                    <div class="max-h-[460px] overflow-auto flex items-center justify-center p-4 bg-white rounded-xl">
                      <img [src]="doc.dataUrl" [alt]="doc.fileName" class="max-h-[420px] max-w-full object-contain rounded-lg shadow-xs" />
                    </div>
                  } @else {
                    <div class="p-8 text-center bg-white rounded-xl space-y-3">
                      <div class="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-800 mx-auto flex items-center justify-center font-extrabold uppercase">
                        {{ doc.fileType }}
                      </div>
                      <div>
                        <div class="font-bold text-sm text-slate-900">{{ doc.fileName }}</div>
                        <p class="text-xs text-slate-500 mt-1">This document format is best previewed by downloading directly.</p>
                      </div>
                      <button
                        (click)="downloadParsedDocument(doc)"
                        class="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-sm"
                      >
                        Download {{ doc.fileName }}
                      </button>
                    </div>
                  }
                </div>
              } @else {
                <div class="p-8 rounded-2xl bg-slate-50 border border-slate-200 text-center text-slate-400">
                  <svg class="w-8 h-8 mx-auto text-slate-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>No application letter attachment uploaded for this institution.</span>
                </div>
              }
            </div>

            <!-- Modal Action Buttons for Super Admin -->
            <div class="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
              <button
                (click)="showInspectModal.set(false)"
                class="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs"
              >
                Close
              </button>

              @if (authService.canManageOnboarding() && (inst.status === OnboardingStatus.PENDING || inst.status === OnboardingStatus.ON_PROGRESS || inst.status === OnboardingStatus.CORRECTION)) {
                <button
                  (click)="showInspectModal.set(false); openDecisionModal(inst.uid, 'reject')"
                  class="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs shadow-2xs"
                >
                  Reject Application
                </button>
                <button
                  (click)="showInspectModal.set(false); openDecisionModal(inst.uid, 'correction')"
                  class="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-white font-bold text-xs shadow-2xs"
                >
                  Return for Correction
                </button>
                <button
                  (click)="showInspectModal.set(false); onApproveOnboarding(inst.uid)"
                  class="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-sm"
                >
                  Approve Institution
                </button>
              }
            </div>
          </div>
        }
      </app-modal>

      <!-- SUPER ADMIN DECISION MODAL (REJECT / RETURN FOR CORRECTION) -->
      <app-modal
        [isOpen]="showDecisionModal()"
        [title]="decisionMode() === 'correction' ? 'Return Application for Correction' : 'Reject Institution Onboarding'"
        maxWidth="max-w-xl"
        zIndex="z-[60]"
        (close)="showDecisionModal.set(false)"
      >
        <div class="space-y-4 text-xs">
          <div class="p-3 rounded-xl" [ngClass]="decisionMode() === 'correction' ? 'bg-amber-50 border border-amber-200 text-amber-900' : 'bg-rose-50 border border-rose-200 text-rose-900'">
            @if (decisionMode() === 'correction') {
              <p>The applicant will be sent an automated notification with your specific instructions and a direct link to edit and resubmit their application.</p>
            } @else {
              <p>Please state the official reason for rejecting this application. An automated notification will be sent to the institution contact person.</p>
            }
          </div>

          <div>
            <label class="block font-bold text-slate-700 uppercase mb-1">
              {{ decisionMode() === 'correction' ? 'Correction Remarks / Missing Documents *' : 'Rejection Reason *' }}
            </label>
            <textarea
              [(ngModel)]="decisionReason"
              rows="4"
              [placeholder]="decisionMode() === 'correction' ? 'e.g. Please upload an official application letter signed by the Permanent Secretary, and update the official phone number...' : 'e.g. Ineligible sector classification or unverified registration credentials...'"
              class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:outline-none focus:border-amber-500"
            ></textarea>
          </div>

          <div class="flex items-center justify-end gap-3 pt-3 border-t border-slate-200">
            <button
              (click)="showDecisionModal.set(false)"
              class="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs"
            >
              Cancel
            </button>
            <button
              (click)="onConfirmDecision()"
              [disabled]="isSubmittingDecision || !decisionReason.trim()"
              class="px-5 py-2 rounded-xl text-white font-bold text-xs shadow-sm disabled:opacity-50 inline-flex items-center gap-1.5"
              [ngClass]="decisionMode() === 'correction' ? 'bg-amber-600 hover:bg-amber-500' : 'bg-rose-600 hover:bg-rose-500'"
            >
              @if (isSubmittingDecision) {
                <svg class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              }
              <span>{{ decisionMode() === 'correction' ? 'Send Return for Correction' : 'Confirm Rejection' }}</span>
            </button>
          </div>
        </div>
      </app-modal>
      </div>
    </div>
  `
})
export class OnboardingComponent implements OnInit {
  onboardingService = inject(OnboardingService);
  toastService = inject(ToastService);
  authService = inject(AuthService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  sanitizer = inject(DomSanitizer);

  OnboardingStatus = OnboardingStatus;
  memberClasses: MemberClass[] = [];

  activeView = signal<'register' | 'lookup' | 'queue' | 'edit'>('register');
  currentStep = signal<number>(1);
  isSubmitting = false;

  // Selected File Metadata for Display
  selectedFileName = '';
  selectedFileExt = '';
  selectedFileSize = '';

  registerForm: OnboardingRegisterRequest = {
    institutionName: '',
    voteNumber: '',
    memberClassCode: 'GOV',
    institutionEmail: '',
    institutionPhone: '',
    firstName: '',
    middleName: '',
    lastName: '',
    phone: '',
    applicantEmail: ''
  };

  formError = signal<ModalErrorState | null>(null);
  submittedOnboarding = signal<OnboardingResponse | null>(null);

  instPhoneError = signal<string>('');
  repPhoneError = signal<string>('');

  lookupQuery = '';
  isSearchingStatus = false;
  lookupResult = signal<OnboardingResponse | null>(null);
  lookupError = signal<{ message: string; code?: string } | null>(null);

  pendingQueue = signal<OnboardingResponse[]>([]);

  // Inspection & Preview Modal State
  showInspectModal = signal<boolean>(false);
  selectedInspection = signal<OnboardingResponse | null>(null);
  parsedDocument = signal<ParsedDocument | null>(null);
  sanitizedDocumentUrl = signal<SafeResourceUrl | null>(null);

  // Decision Modal State (Reject / Return for Correction)
  showDecisionModal = signal<boolean>(false);
  decisionMode = signal<'reject' | 'correction'>('correction');
  decisionReason = '';
  decisionUid = '';
  isSubmittingDecision = false;

  // Edit Resubmission State
  editTarget = signal<OnboardingResponse | null>(null);

  ngOnInit() {
    this.memberClasses = this.onboardingService.memberClasses;
    this.loadMemberClasses();
    this.loadQueue();

    // Check query params for direct links: ?token=...&action=edit or ?view=lookup
    this.route.queryParams.subscribe(params => {
      if (params['token']) {
        const token = params['token'].trim();
        this.lookupQuery = token;
        this.loadApplicationByToken(token, params['action'] === 'edit');
      } else if (params['view'] === 'lookup') {
        this.activeView.set('lookup');
      } else if (params['view'] === 'queue' && this.authService.canManageOnboarding()) {
        this.activeView.set('queue');
      } else if (!params['view'] && this.authService.canManageOnboarding()) {
        this.activeView.set('queue');
      }
    });
  }

  loadMemberClasses() {
    this.onboardingService.getMemberClasses().subscribe({
      next: (res) => {
        if (res.success && res.data && res.data.length > 0) {
          this.memberClasses = res.data;
          if (!this.registerForm.memberClassCode && this.memberClasses.length > 0) {
            this.registerForm.memberClassCode = this.memberClasses[0].code;
          }
        }
      },
      error: () => {
        this.memberClasses = this.onboardingService.memberClasses;
      }
    });
  }

  loadQueue() {
    this.onboardingService.getAllOnboardings().subscribe(res => {
      if (res.success && res.data) {
        this.pendingQueue.set(res.data);
      }
    });
  }

  loadApplicationByToken(token: string, autoEdit = false) {
    this.isSearchingStatus = true;
    this.onboardingService.getOnboardingByToken(token).subscribe({
      next: (res) => {
        this.isSearchingStatus = false;
        if (res.success && res.data) {
          this.lookupResult.set(res.data);
          if (autoEdit || res.data.status === OnboardingStatus.CORRECTION) {
            this.startCorrectionEdit(res.data);
          } else {
            this.activeView.set('lookup');
          }
        }
      },
      error: () => {
        this.isSearchingStatus = false;
        this.toastService.error('Invalid or expired application token: ' + token);
      }
    });
  }

  trackSpecificToken(token: string | undefined) {
    if (!token) return;
    this.lookupQuery = token;
    this.activeView.set('lookup');
    this.onLookupStatus();
  }

  validateInstitutionPhone(): boolean {
    const phone = this.registerForm.institutionPhone;
    if (!phone || !phone.trim()) {
      this.instPhoneError.set('Official institution phone number is required.');
      return false;
    }
    if (!isValidPhoneNumber(phone)) {
      this.instPhoneError.set('Official institution phone must include a valid country code and 9-digit number (e.g. +255 777 000 000).');
      return false;
    }
    this.instPhoneError.set('');
    return true;
  }

  validateRepPhone(): boolean {
    const phone = this.registerForm.phone;
    if (!phone || !phone.trim()) {
      this.repPhoneError.set('Representative phone number is required.');
      return false;
    }
    if (!isValidPhoneNumber(phone)) {
      this.repPhoneError.set('Representative phone must include a valid country code and 9-digit number (e.g. +255 777 000 000).');
      return false;
    }
    this.repPhoneError.set('');
    return true;
  }

  validateEmail(email: string): boolean {
    if (!email || !email.trim()) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }

  goToStep(step: number) {
    if (step === 2) {
      const isInstPhoneValid = this.validateInstitutionPhone();
      if (!this.registerForm.institutionName?.trim()) {
        this.toastService.warning('Please enter official institution name.');
        return;
      }
      if (!this.registerForm.institutionEmail?.trim()) {
        this.toastService.warning('Please enter official institution email.');
        return;
      }
      if (!this.validateEmail(this.registerForm.institutionEmail)) {
        this.toastService.warning('Please enter a valid institution email address.');
        return;
      }
      if (!isInstPhoneValid) {
        this.toastService.warning(this.instPhoneError() || 'Please enter a valid official institution phone number.');
        return;
      }
    }
    if (step === 3) {
      const isRepPhoneValid = this.validateRepPhone();
      if (!this.registerForm.firstName?.trim() || !this.registerForm.lastName?.trim()) {
        this.toastService.warning('Please fill in representative first name and last name.');
        return;
      }
      if (!isRepPhoneValid) {
        this.toastService.warning(this.repPhoneError() || 'Please enter a valid representative phone number.');
        return;
      }
      if (this.registerForm.applicantEmail && !this.validateEmail(this.registerForm.applicantEmail)) {
        this.toastService.warning('Please enter a valid applicant direct email address.');
        return;
      }
    }
    this.currentStep.set(step);
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      this.selectedFileName = file.name;
      const fileExt = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : 'doc';
      this.selectedFileExt = fileExt;
      this.selectedFileSize = (file.size / 1024).toFixed(1) + ' KB';

      const reader = new FileReader();
      reader.onload = () => {
        const resultString = reader.result as string;
        // Extract raw pure base64 string without data:*;base64, header prefix
        const base64Content = resultString.includes(',') ? resultString.split(',')[1] : resultString;
        this.registerForm.applicationLetter = {
          content: base64Content,
          fileType: fileExt || 'doc'
        };
        this.toastService.info(`Application letter attached: ${file.name}`);
      };
      reader.readAsDataURL(file);
    }
  }

  removeSelectedFile() {
    this.selectedFileName = '';
    this.selectedFileExt = '';
    this.selectedFileSize = '';
    this.registerForm.applicationLetter = undefined;
  }

  onSubmitRegistration() {
    if (!this.validateInstitutionPhone()) {
      this.toastService.error(this.instPhoneError());
      this.goToStep(1);
      return;
    }
    if (!this.validateRepPhone()) {
      this.toastService.error(this.repPhoneError());
      this.goToStep(2);
      return;
    }
    if (!this.registerForm.applicationLetter || !this.registerForm.applicationLetter.content) {
      this.toastService.error('Please upload your official application letter before submitting.');
      return;
    }

    this.formError.set(null);
    this.isSubmitting = true;
    this.onboardingService.registerInstitution(this.registerForm).subscribe({
      next: (res) => {
        this.isSubmitting = false;
        if (res.success && res.data) {
          this.submittedOnboarding.set(res.data);
          this.currentStep.set(4);
          this.toastService.success(res.message || 'Onboarding application registered successfully!');
          this.loadQueue();
        } else if (res.success === false) {
          this.formError.set(extractErrorDetails(res));
        }
      },
      error: (err) => {
        this.isSubmitting = false;
        this.formError.set(extractErrorDetails(err.error || err));
      }
    });
  }

  onLookupStatus() {
    this.lookupError.set(null);
    if (!this.lookupQuery || !this.lookupQuery.trim()) {
      this.toastService.warning('Please enter your secret application tracking token.');
      return;
    }
    this.isSearchingStatus = true;
    this.lookupResult.set(null);
    this.onboardingService.lookupStatus(this.lookupQuery.trim()).subscribe({
      next: (res) => {
        this.isSearchingStatus = false;
        if (res.success && res.data) {
          this.lookupResult.set(res.data);
          this.lookupError.set(null);
          this.toastService.info('Application status record found');
        } else {
          const errMsg = res.message || 'Invalid tracking token. No application record found.';
          this.lookupError.set({ message: errMsg, code: res.code });
          this.toastService.error(errMsg);
        }
      },
      error: (err) => {
        this.isSearchingStatus = false;
        const backendMessage = err.error?.message || err.message || 'Invalid tracking token. Please enter the secret application token provided during registration.';
        const backendCode = err.error?.code || (err.status ? `HTTP ${err.status}` : undefined);
        this.lookupError.set({ message: backendMessage, code: backendCode });
        this.toastService.error(backendMessage);
      }
    });
  }

  startCorrectionEdit(item: OnboardingResponse) {
    this.editTarget.set(item);
    this.registerForm = {
      institutionName: item.institutionName || '',
      voteNumber: item.voteNumber || '',
      registrationNumber: item.registrationNumber || '',
      memberClassCode: item.memberClassCode || 'GOV',
      institutionEmail: item.institutionEmail || '',
      institutionPhone: item.institutionPhone || '',
      firstName: item.firstName || '',
      middleName: item.middleName || '',
      lastName: item.lastName || '',
      phone: item.phone || '',
      applicantEmail: item.applicantEmail || item.institutionEmail || ''
    };
    this.selectedFileName = '';
    this.activeView.set('edit');
  }

  onSubmitResubmission(token: string) {
    if (!this.validateInstitutionPhone()) {
      this.toastService.error(this.instPhoneError());
      return;
    }
    if (!this.validateRepPhone()) {
      this.toastService.error(this.repPhoneError());
      return;
    }

    this.isSubmitting = true;
    this.onboardingService.resubmitOnboarding(token, this.registerForm).subscribe({
      next: (res) => {
        this.isSubmitting = false;
        if (res.success && res.data) {
          this.toastService.success('Application resubmitted successfully! It is now under regulator review.');
          this.lookupResult.set(res.data);
          this.activeView.set('lookup');
          this.loadQueue();
        }
      },
      error: (err) => {
        this.isSubmitting = false;
        this.toastService.error(err.error?.message || 'Failed to resubmit application. Please verify all fields.');
      }
    });
  }

  // Inspection & Document Parsing
  onInspectOnboarding(onb: OnboardingResponse) {
    this.selectedInspection.set(onb);
    const parsed = this.parseApplicationDocument(onb.applicationLetter);
    this.parsedDocument.set(parsed);
    if (parsed && parsed.isPdf) {
      this.sanitizedDocumentUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(parsed.dataUrl));
    } else {
      this.sanitizedDocumentUrl.set(null);
    }
    this.showInspectModal.set(true);
  }

  inspectItemDocument(item: OnboardingResponse) {
    this.onInspectOnboarding(item);
  }

  parseApplicationDocument(appLetterStr?: string): ParsedDocument | null {
    if (!appLetterStr || !appLetterStr.trim()) return null;
    let content = '';
    let fileType = 'pdf';
    let fileName = 'application_letter.pdf';

    try {
      if (appLetterStr.trim().startsWith('{')) {
        const parsed = JSON.parse(appLetterStr);
        content = parsed.content || '';
        fileType = (parsed.fileType || 'pdf').toLowerCase().replace('.', '');
      } else {
        content = appLetterStr;
      }
    } catch {
      content = appLetterStr;
    }

    if (!content) return null;

    let mimeType = 'application/pdf';
    let isPdf = false;
    let isImage = false;

    if (fileType === 'pdf') {
      mimeType = 'application/pdf';
      isPdf = true;
    } else if (fileType === 'png') {
      mimeType = 'image/png';
      isImage = true;
    } else if (fileType === 'jpg' || fileType === 'jpeg') {
      mimeType = 'image/jpeg';
      isImage = true;
    } else if (fileType === 'doc' || fileType === 'docx') {
      mimeType = 'application/msword';
    }

    const dataUrl = content.startsWith('data:') ? content : `data:${mimeType};base64,${content}`;
    fileName = `application_letter.${fileType}`;

    return { content, fileType, dataUrl, fileName, isPdf, isImage };
  }

  downloadParsedDocument(doc: ParsedDocument) {
    const link = document.createElement('a');
    link.href = doc.dataUrl;
    link.download = doc.fileName;
    link.click();
  }

  // Super Admin Decision Actions
  openDecisionModal(uid: string, mode: 'reject' | 'correction') {
    if (!this.authService.canManageOnboarding()) {
      this.toastService.error('Unauthorized: Registration decisions require onboarding management permissions.');
      return;
    }
    this.decisionUid = uid;
    this.decisionMode.set(mode);
    this.decisionReason = '';
    this.showDecisionModal.set(true);
  }

  onConfirmDecision() {
    if (!this.authService.canManageOnboarding()) {
      this.toastService.error('Unauthorized: Registration decisions require onboarding management permissions.');
      return;
    }

    if (!this.decisionReason.trim()) {
      this.toastService.warning('Please enter reason or remarks.');
      return;
    }

    this.isSubmittingDecision = true;
    const req = { reason: this.decisionReason.trim() };

    if (this.decisionMode() === 'correction') {
      this.onboardingService.returnForCorrection(this.decisionUid, req).subscribe({
        next: (res) => {
          this.isSubmittingDecision = false;
          this.showDecisionModal.set(false);
          this.toastService.success('Application returned for correction. Notification sent to institution.');
          if (res.data) {
            this.lookupResult.set(res.data);
          }
          this.loadQueue();
        },
        error: (err) => {
          this.isSubmittingDecision = false;
          this.toastService.error(err.error?.message || 'Failed to return application for correction.');
        }
      });
    } else {
      this.onboardingService.rejectOnboarding(this.decisionUid, req).subscribe({
        next: (res) => {
          this.isSubmittingDecision = false;
          this.showDecisionModal.set(false);
          this.toastService.success('Application has been rejected. Notification sent to applicant.');
          if (res.data) {
            this.lookupResult.set(res.data);
          }
          this.loadQueue();
        },
        error: (err) => {
          this.isSubmittingDecision = false;
          this.toastService.error(err.error?.message || 'Failed to reject application.');
        }
      });
    }
  }

  async onApproveOnboarding(uid: string) {
    if (!this.authService.canManageOnboarding()) {
      this.toastService.error('Unauthorized: Only authorized administrators can approve institution onboarding.');
      return;
    }

    const item = this.pendingQueue().find(q => q.uid === uid) || this.selectedInspection() || (this.lookupResult()?.uid === uid ? this.lookupResult() : null);
    const instTitle = item?.institutionName ? ` for "${item.institutionName}"` : '';

    const confirmed = await this.toastService.confirm({
      title: 'Approve Institution Onboarding',
      message: `Are you sure you want to approve onboarding${instTitle} and provision network access on Zan-X-Change?`,
      confirmText: 'Yes, Approve',
      cancelText: 'Cancel',
      confirmStyle: 'success'
    });
    if (!confirmed) return;

    this.onboardingService.approveOnboarding(uid).subscribe({
      next: (res) => {
        if (res.success) {
          this.toastService.success(res.message || 'Institution onboarding approved successfully!');
          if (res.data) {
            this.lookupResult.set(res.data);
          }
          this.loadQueue();
        }
      },
      error: (err) => {
        this.toastService.error(err.error?.message || 'Failed to approve institution.');
      }
    });
  }
}
